import { Stack, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { GoalAmountSection } from "web_myplann/Goals/Components/AmountSections/GoalAmountSection";
import { GoalCategorySelector } from "web_myplann/Goals/Components/GoalCategorySelector";
import { DUMMY_ACTIVE_GOALS } from "web_myplann/Goals/DummyData/goalsDummyData";
import {
	type GoalFormModel,
	useGoalForm,
} from "web_myplann/Goals/Forms/useGoalForm";
import { useGoalCategoryStrings } from "web_myplann/Goals/Hooks/useGoalCategoryStrings";
import { type Goal, GoalCategory } from "web_myplann/Goals/Types/GoalTypes";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { CollapsibleSection } from "web_myplann/Shared/Components/CollapsibleSection";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { FormSecondaryButton } from "web_myplann/Shared/Components/FormSecondaryButton";
import { FormSubmitButton } from "web_myplann/Shared/Components/FormSubmitButton";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";

const findGoalById = (id: string): Goal | undefined =>
	DUMMY_ACTIVE_GOALS.find((goal) => goal.id === id);

const parseCategoryParam = (raw: string | null): GoalCategory | undefined => {
	if (!raw) return undefined;
	return (Object.values(GoalCategory) as string[]).includes(raw)
		? (raw as GoalCategory)
		: undefined;
};

export const GoalForm = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const existingGoal = id ? findGoalById(id) : undefined;
	const isEditMode = Boolean(id);

	const [searchParams] = useSearchParams();
	const initialCategory = parseCategoryParam(searchParams.get("category"));

	const { control, handleSubmit, watch, setValue, getValues } = useGoalForm({
		goal: existingGoal,
		initialCategory,
	});

	const minDueDate = useMemo(() => new Date(), []);
	const category = watch("category");
	const totalValue = watch("totalValue");
	const submitDisabled = !totalValue || totalValue <= 0;

	const { defaultName, getDefaultName } = useGoalCategoryStrings(category);

	const lastSuggestedNameRef = useRef<string>(
		getDefaultName(existingGoal?.category),
	);

	useEffect(() => {
		if (!category) return;
		const currentName = getValues("name") ?? "";
		if (currentName === "" || currentName === lastSuggestedNameRef.current) {
			setValue("name", defaultName, {
				shouldDirty: false,
				shouldValidate: false,
			});
		}
		lastSuggestedNameRef.current = defaultName;
	}, [category, defaultName, getValues, setValue]);

	const goToList = () => {
		navigate(getRoute(Routing.Goals.GoalsList));
	};

	const onSubmit = (_data: GoalFormModel) => {
		goToList();
	};

	return (
		<MyPlannLayout
			bgColor={theme.palette.grey[50]}
			disableContentPadding
			topBar={
				<NavigationHeader
					defaultRoute={Routing.Goals.GoalsList}
					title={
						isEditMode
							? t(Resources.Goals.Form.titleEdit)
							: t(Resources.Goals.Form.titleCreate)
					}
					bgColor={theme.palette.common.white}
				/>
			}
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<Stack>
					<CollapsibleSection title={t(Resources.Goals.Form.sectionCategory)}>
						<GoalCategorySelector control={control} name="category" />
					</CollapsibleSection>

					<CollapsibleSection title={t(Resources.Goals.Form.sectionAbout)}>
						<Stack bgcolor={theme.palette.background.paper}>
							<FlatRowFormInput
								control={control}
								name="name"
								label={t(Resources.Goals.Form.nameLabel)}
								placeholder={t(Resources.Goals.Form.namePlaceholder)}
							/>
							<FlatRowFormDatePicker
								control={control}
								name="dueDate"
								label={t(Resources.Goals.Form.dueDateLabel)}
								minDate={minDueDate}
								isLast
							/>
						</Stack>
					</CollapsibleSection>

					<CollapsibleSection title={t(Resources.Goals.Form.sectionAmount)}>
						<GoalAmountSection control={control} category={category} />
					</CollapsibleSection>

					<CollapsibleSection title={t(Resources.Goals.Form.sectionNote)}>
						<Stack bgcolor={theme.palette.background.paper}>
							<FlatRowFormTextarea
								control={control}
								name="note"
								label={t(Resources.Goals.Form.noteLabel)}
								placeholder={t(Resources.Goals.Form.notePlaceholder)}
								rows={1}
								isLast
							/>
						</Stack>
					</CollapsibleSection>

					<Stack px={1.6} pt={2.4} pb={3.2} gap={1.2}>
						<FormSubmitButton type="submit" disabled={submitDisabled}>
							{isEditMode
								? t(Resources.Goals.Form.submitEdit)
								: t(Resources.Goals.Form.submitCreate)}
						</FormSubmitButton>
						<FormSecondaryButton onClick={goToList}>
							{t(Resources.Goals.Form.cancel)}
						</FormSecondaryButton>
					</Stack>
				</Stack>
			</form>
		</MyPlannLayout>
	);
};
